/**
 * Registry of curated, built-in MCP connectors — remote MCP servers we ship as
 * one-click connectors. Each entry rides the generic OAuth-MCP subsystem
 * (`src/utils/mcp-oauth.ts`) + the dynamic toolset builder
 * (`src/tools/builtin-mcp.ts`), so adding a new MCP connector is (in the common
 * OAuth case) just one entry here — no per-connector route/gateway/tool wiring.
 *
 * Constraints:
 *  - `id` must be a short slug with NO underscores: the chat tool prefix is
 *    `mcp_<id>_`, and the connector-gateway reveal derives the service id from the
 *    first two `_`-delimited segments (so `mcp_<id>` must be exactly two segments).
 *    Hyphens are fine (they're stripped when slugging).
 *  - `icon` is a key into CONNECTOR_ICONS (equals `id`) or a remote image URL.
 *
 * This module is imported by both server (routes, tools) and client (icons,
 * ConnectorsModal) code, so keep it free of server-only imports.
 */
import { BuiltinMcpConnector } from 'types';

export const BUILTIN_MCP_CONNECTORS: BuiltinMcpConnector[] = [
  {
    id: 'posthog',
    name: 'PostHog',
    description:
      'Query product analytics, insights, dashboards, flags and errors',
    longDescription:
      'Connect PostHog to answer product questions from your live analytics: run SQL/HogQL queries, explore your event schema, create and read insights and dashboards, inspect error tracking, and manage feature flags and experiments. Powers data-grounded reports for Marky and AI Employees (e.g. a daily business brief with real metrics). Uses the US cloud endpoint (mcp.posthog.com); note that some PostHog MCP tools use LLMs internally and can count as PostHog AI spend.',
    icon: 'posthog',
    serverUrl: 'https://mcp.posthog.com/mcp',
    transport: 'http',
    // PostHog's MCP endpoint implements full MCP OAuth (RFC 9728 discovery →
    // oauth.posthog.com with DCR + PKCE S256 + refresh tokens; probed
    // 2026-08-13). Scopes omitted → the server's own consent defaults apply.
    auth: { type: 'oauth' },
    website: 'https://posthog.com',
    docs: 'https://posthog.com/docs/model-context-protocol',
    privacy: 'https://posthog.com/privacy',
    displayTools: [
      'query-run',
      'read-data-schema',
      'insight-create-from-query',
      'insights-get-all',
      'dashboards-get-all',
      'dashboard-create',
      'list-errors',
      'feature-flag-create',
    ],
  },
  {
    id: 'mixpanel',
    name: 'Mixpanel',
    description:
      'Query product analytics: insights, funnels, retention, dashboards',
    longDescription:
      'Connect Mixpanel to answer product questions from your live analytics: run insights, funnel, flow and retention queries, read saved reports, browse projects, events and properties, and create or update dashboards. You sign in with your Mixpanel account and keep your existing project permissions. For projects hosted in the US data center (mixpanel.com); use "Mixpanel (EU)" if your project lives on eu.mixpanel.com.',
    icon: 'mixpanel',
    // Official Mixpanel-hosted MCP. Documented as full MCP OAuth: RFC 9728 +
    // RFC 8414 discovery, dynamic client registration, Authorization Code +
    // PKCE S256, refresh tokens. NOT probed from here (mixpanel.com is
    // egress-blocked in the cloud sessions) — confirm DCR accepts our
    // redirect URI on the first real connect, the way Meta/Dropbox were
    // checked. Scopes omitted → Mixpanel's own consent defaults apply.
    serverUrl: 'https://mcp.mixpanel.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://mixpanel.com',
    docs: 'https://docs.mixpanel.com/docs/mcp',
    privacy: 'https://mixpanel.com/legal/privacy-policy/',
    displayTools: [
      'Get-Projects',
      'Get-Events',
      'Get-Query-Schema',
      'Run-Query',
      'Get-Report',
      'Create-Dashboard',
    ],
  },
  {
    // Same server on Mixpanel's EU data center. Kept as a separate card (the
    // Supabase read-only/read-write precedent) because the region is fixed by
    // the host and an EU project cannot be reached through the US endpoint.
    // India residency (mcp-in.mixpanel.com) is the same pattern if needed.
    id: 'mixpanel-eu',
    name: 'Mixpanel (EU)',
    description: 'Mixpanel analytics for projects in the EU data center',
    longDescription:
      'Connect Mixpanel projects hosted in the EU data center (eu.mixpanel.com): run insights, funnel, flow and retention queries, read saved reports, browse events and properties, and create or update dashboards. You sign in with your Mixpanel account and keep your existing project permissions.',
    icon: 'mixpanel',
    serverUrl: 'https://mcp-eu.mixpanel.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://mixpanel.com',
    docs: 'https://docs.mixpanel.com/docs/mcp',
    privacy: 'https://mixpanel.com/legal/privacy-policy/',
    displayTools: [
      'Get-Projects',
      'Get-Events',
      'Get-Query-Schema',
      'Run-Query',
      'Get-Report',
      'Create-Dashboard',
    ],
  },
  {
    id: 'supabase',
    name: 'Supabase (Read-only)',
    description: 'Query your Supabase projects and databases — read-only',
    longDescription:
      'Connect Supabase in READ-ONLY mode: run SQL queries as a read-only Postgres user, inspect schemas, logs and advisors, and search Supabase docs — with no risk of writes. Safest choice for analytics and reporting; connect "Supabase (Read & Write)" instead if the assistant should modify data or manage projects.',
    icon: 'supabase',
    // read_only=true makes the MCP server execute everything as a read-only
    // Postgres user (server-enforced, not just prompt-enforced).
    serverUrl: 'https://mcp.supabase.com/mcp?read_only=true',
    transport: 'http',
    // Supabase MCP implements MCP OAuth with Dynamic Client Registration.
    // Read-only must hold at BOTH layers: the URL flag constrains the MCP
    // server's execution, and these :read-only scopes constrain the OAuth
    // TOKEN itself — without them the consent screen shows (and grants)
    // Supabase's default READ+WRITE scopes, which is exactly what a user who
    // picked the read-only card doesn't want.
    auth: {
      type: 'oauth',
      scopes: [
        'organizations:read',
        'projects:read',
        'database:read',
        'analytics:read',
        'secrets:read',
        'edge_functions:read',
        'environment:read',
        'storage:read',
      ],
    },
    website: 'https://supabase.com',
    docs: 'https://supabase.com/docs/guides/ai-tools/mcp',
    privacy: 'https://supabase.com/privacy',
    displayTools: [
      'list_projects',
      'list_tables',
      'execute_sql',
      'get_logs',
      'get_advisors',
      'search_docs',
    ],
  },
  {
    id: 'supabase-rw',
    name: 'Supabase (Read & Write)',
    description: 'Query AND modify your Supabase projects and databases',
    longDescription:
      'Connect Supabase with FULL access: run any SQL (including writes and migrations), manage projects, branches and Edge Functions. Grant this only to assistants/employees that genuinely need to change data — for reporting and analysis, prefer "Supabase (Read-only)".',
    icon: 'supabase',
    serverUrl: 'https://mcp.supabase.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://supabase.com',
    docs: 'https://supabase.com/docs/guides/ai-tools/mcp',
    privacy: 'https://supabase.com/privacy',
    displayTools: [
      'list_projects',
      'execute_sql',
      'apply_migration',
      'create_branch',
      'deploy_edge_function',
      'get_logs',
    ],
  },
  {
    id: 'crisp',
    name: 'Crisp',
    description: 'Read and manage conversations in your Crisp workspace',
    longDescription:
      'Connect your Crisp customer-messaging workspace: read and search conversations, check operator availability, update conversation states and send messages — ideal for a Customer Support employee working your Crisp inbox. Uses a Crisp MCP Server Token (Crisp dashboard → Settings → Workspace Settings → Advanced Configuration → MCP Server Token).',
    icon: 'crisp',
    serverUrl: 'https://api.crisp.chat/mcp/',
    transport: 'http',
    // Crisp uses a static workspace token sent as a Bearer header — collected
    // via the token-connect flow (no OAuth on their MCP endpoint).
    auth: {
      type: 'headers',
      fields: [{ name: 'token', label: 'MCP Server Token' }],
    },
    website: 'https://crisp.chat',
    docs: 'https://docs.crisp.chat/guides/mcp-server/',
    privacy: 'https://crisp.chat/en/privacy/',
    displayTools: ['get_website', 'list_conversations', 'send_message'],
  },
  {
    id: 'meta-ads',
    name: 'Meta Ads',
    description:
      'Manage Facebook & Instagram ads: campaigns, creatives, insights',
    longDescription:
      'Connect your Meta (Facebook & Instagram) ad accounts to pull campaign performance and insights, browse and create campaigns, ad sets, ads and creatives, and research audiences (interests, behaviors, demographics, locations) — all from chat. Served through Pipeboard, a badged Meta Business Partner: you sign in with your Facebook account via Pipeboard, which brokers access to the Meta Marketing API (free Pipeboard plan available).',
    icon: 'meta-ads',
    // Meta's OFFICIAL Ads MCP (https://mcp.facebook.com/ads) implements MCP
    // OAuth but its DCR endpoint is ALLOWLISTED by client identity — probed
    // 2026-08-14: registering as "Easy-Peasy.AI" → "Dynamic registration is
    // not available for this client", while known AI clients (Claude/ChatGPT/
    // Perplexity) register fine. Until Meta allowlists us, we ship the
    // Pipeboard-hosted server (open DCR + PKCE S256 + refresh, probed same
    // day — our production redirect URI registered successfully). If Meta
    // ever opens up, swapping serverUrl to https://mcp.facebook.com/ads
    // should be the only change (scopes advertised in its WWW-Authenticate:
    // ads_management ads_read catalog_management business_management
    // pages_show_list instagram_basic ads_mcp_management).
    serverUrl: 'https://meta-ads.mcp.pipeboard.co/',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://pipeboard.co',
    docs: 'https://github.com/pipeboard-co/meta-ads-mcp',
    privacy: 'https://pipeboard.co/privacy-policy',
    displayTools: [
      'get_ad_accounts',
      'get_campaigns',
      'get_insights',
      'create_campaign',
      'create_adset',
      'create_ad',
      'search_interests',
      'upload_ad_image',
    ],
  },
  {
    id: 'wix',
    name: 'Wix',
    description: 'Manage and build sites and apps on Wix',
    longDescription:
      "Connect Wix to manage your account's websites and apps (Stores, Bookings, Blog, and more), call Wix site APIs, and search the Wix REST/SDK/Headless documentation — all from chat.",
    icon: 'wix',
    serverUrl: 'https://mcp.wix.com/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['offline_access'] },
    website: 'https://www.wix.com',
    docs: 'https://dev.wix.com/docs/sdk/articles/use-the-wix-mcp/about-the-wix-mcp',
    privacy: 'https://www.wix.com/about/privacy',
    displayTools: [
      'SearchWixRESTDocumentation',
      'SearchWixSDKDocumentation',
      'SearchWixHeadlessDocumentation',
      'ReadFullDocsArticle',
      'ReadFullDocsMethodSchema',
      'ListWixSites',
      'CallWixSiteAPI',
      'ManageWixSite',
    ],
  },
  {
    id: 'linear',
    name: 'Linear',
    description: 'Track issues, projects, and cycles in Linear',
    longDescription:
      'Connect Linear to search and create issues, browse projects and cycles, manage your teams, and update work — all from chat.',
    icon: 'linear',
    serverUrl: 'https://mcp.linear.app/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['read', 'write'] },
    website: 'https://linear.app',
    docs: 'https://linear.app/developers',
    privacy: 'https://linear.app/privacy',
    displayTools: [
      'list_issues',
      'create_issue',
      'update_issue',
      'list_projects',
      'list_teams',
    ],
  },
  {
    id: 'asana',
    name: 'Asana',
    description: 'Manage tasks, projects, and portfolios in Asana',
    longDescription:
      'Connect Asana to search tasks, create and update tasks, browse projects and portfolios, and keep work moving — all from chat.',
    icon: 'asana',
    serverUrl: 'https://mcp.asana.com/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['default'] },
    website: 'https://asana.com',
    docs: 'https://developers.asana.com/docs/using-asanas-mcp-server',
    privacy: 'https://asana.com/terms#privacy-policy',
    displayTools: [
      'search_tasks',
      'create_task',
      'update_task',
      'list_projects',
      'get_task',
    ],
  },
  {
    id: 'monday',
    name: 'monday.com',
    description: 'Manage boards, items, and updates in monday.com',
    longDescription:
      'Connect monday.com to read and update boards, create and move items, post updates, and coordinate your team workflows — all from chat.',
    icon: 'monday',
    serverUrl: 'https://mcp.monday.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://monday.com',
    docs: 'https://developer.monday.com/apps/docs/mcp',
    privacy: 'https://monday.com/l/privacy/privacy-policy/',
    displayTools: [
      'list_boards',
      'get_board_items',
      'create_item',
      'update_item',
      'create_update',
    ],
  },
  {
    id: 'trello',
    name: 'Trello',
    description: 'Manage boards, lists, and cards in Trello',
    longDescription:
      'Connect Trello (by Atlassian) to browse your boards, create and update cards and lists, move work along, and search across a workspace — all from chat. Safe by design: the official Trello MCP server does not allow destructive deletes (boards, lists, cards, labels, comments and attachments cannot be deleted through it), and one connection covers one Trello workspace.',
    icon: 'trello',
    // Official Atlassian-hosted Trello MCP. Full MCP OAuth: RFC 9728 metadata
    // → auth.atlassian.com tenant with open DCR + PKCE S256 + refresh (probed
    // 2026-08-14 — our production redirect URI registered fine). offline_access
    // is required in scope for Atlassian to issue a refresh token.
    serverUrl: 'https://mcp.trello.com/v1',
    transport: 'http',
    auth: {
      type: 'oauth',
      scopes: [
        'offline_access',
        'read:me',
        'read:account',
        'read:board:trello',
        'write:board:trello',
        'read:organization:trello',
        'write:organization:trello',
        'read:member:trello',
        'read:member.inbox:trello',
        'write:member.inbox:trello',
        'read:member.planner:trello',
        'write:member.planner:trello',
      ],
    },
    website: 'https://trello.com',
    docs: 'https://trello.com/mcp',
    privacy: 'https://www.atlassian.com/legal/privacy-policy',
    displayTools: [
      'get_boards',
      'get_lists',
      'get_cards',
      'create_card',
      'update_card',
      'move_card',
      'add_comment',
    ],
  },
  {
    id: 'stripe',
    name: 'Stripe',
    description: 'Query payments, customers, and invoices in Stripe',
    longDescription:
      'Connect Stripe to look up payments, customers, invoices, subscriptions, and balances, and answer billing questions — all from chat.',
    icon: 'stripe',
    serverUrl: 'https://mcp.stripe.com/',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://stripe.com',
    docs: 'https://docs.stripe.com/mcp',
    privacy: 'https://stripe.com/privacy',
    displayTools: [
      'list_payments',
      'list_customers',
      'list_invoices',
      'list_subscriptions',
      'retrieve_balance',
    ],
  },
  {
    id: 'paypal',
    name: 'PayPal',
    description: 'Manage transactions, invoices, and orders in PayPal',
    longDescription:
      'Connect PayPal to look up transactions, create and send invoices, manage orders, and review business activity — all from chat.',
    icon: 'paypal',
    serverUrl: 'https://mcp.paypal.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://www.paypal.com',
    docs: 'https://developer.paypal.com/tools/mcp-server/',
    privacy: 'https://www.paypal.com/legalhub/privacy-full',
    displayTools: [
      'list_transactions',
      'create_invoice',
      'list_invoices',
      'create_order',
      'get_order',
    ],
  },
  {
    id: 'square',
    name: 'Square',
    description: 'Manage orders, catalog, and customers in Square',
    longDescription:
      'Connect Square to look up payments and orders, browse your catalog and inventory, and manage customers — all from chat.',
    icon: 'square',
    serverUrl: 'https://mcp.squareup.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://squareup.com',
    docs: 'https://developer.squareup.com/docs/mcp',
    privacy: 'https://squareup.com/legal/general/privacy',
    displayTools: [
      'list_payments',
      'list_orders',
      'search_catalog',
      'list_customers',
      'list_inventory',
    ],
  },
  {
    id: 'webflow',
    name: 'Webflow',
    description: 'Manage sites, CMS collections, and pages in Webflow',
    longDescription:
      'Connect Webflow to browse your sites, read and edit CMS collections and items, manage pages, and publish — all from chat.',
    icon: 'webflow',
    serverUrl: 'https://mcp.webflow.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://webflow.com',
    docs: 'https://developers.webflow.com/data/docs/ai-tools',
    privacy: 'https://webflow.com/legal/privacy',
    displayTools: [
      'list_sites',
      'list_collections',
      'list_collection_items',
      'create_collection_item',
      'publish_site',
    ],
  },
  {
    id: 'vercel',
    name: 'Vercel',
    description: 'Manage projects, deployments, and domains in Vercel',
    longDescription:
      'Connect Vercel to list projects and deployments, inspect build and runtime logs, manage domains, and check deployment status — all from chat.',
    icon: 'vercel',
    serverUrl: 'https://mcp.vercel.com/',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['openid', 'offline_access'] },
    website: 'https://vercel.com',
    docs: 'https://vercel.com/docs/mcp/vercel-mcp',
    privacy: 'https://vercel.com/legal/privacy-policy',
    displayTools: [
      'list_projects',
      'list_deployments',
      'get_deployment',
      'list_domains',
      'get_deployment_logs',
    ],
  },
  {
    id: 'sentry',
    name: 'Sentry',
    description: 'Investigate errors, issues, and events in Sentry',
    longDescription:
      'Connect Sentry to search issues and events, inspect stack traces, review project health, and triage errors — all from chat.',
    icon: 'sentry',
    serverUrl: 'https://mcp.sentry.dev/mcp',
    transport: 'http',
    auth: {
      type: 'oauth',
      scopes: ['org:read', 'project:write', 'team:write', 'event:write'],
    },
    website: 'https://sentry.io',
    docs: 'https://docs.sentry.io/product/sentry-mcp/',
    privacy: 'https://sentry.io/privacy/',
    displayTools: [
      'find_issues',
      'get_issue_details',
      'list_projects',
      'search_events',
      'update_issue',
    ],
  },
  {
    id: 'klaviyo',
    name: 'Klaviyo',
    description: 'Manage email/SMS campaigns, flows, and segments in Klaviyo',
    longDescription:
      'Connect Klaviyo to browse campaigns and flows, review segments and profiles, and pull marketing performance — all from chat.',
    icon: 'klaviyo',
    serverUrl: 'https://mcp.klaviyo.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://www.klaviyo.com',
    docs: 'https://developers.klaviyo.com/en/docs/klaviyo_mcp_server',
    privacy: 'https://www.klaviyo.com/legal/privacy',
    displayTools: [
      'list_campaigns',
      'list_flows',
      'list_segments',
      'get_profiles',
      'get_metrics',
    ],
  },
  {
    id: 'mailerlite',
    name: 'MailerLite',
    description: 'Manage subscribers, campaigns, and groups in MailerLite',
    longDescription:
      'Connect MailerLite to manage subscribers and groups, review campaigns and automations, and check performance — all from chat.',
    icon: 'mailerlite',
    serverUrl: 'https://mcp.mailerlite.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://www.mailerlite.com',
    docs: 'https://developers.mailerlite.com',
    privacy: 'https://www.mailerlite.com/legal/privacy-policy',
    displayTools: [
      'list_subscribers',
      'list_campaigns',
      'list_groups',
      'create_campaign',
      'add_subscriber',
    ],
  },
  {
    id: 'customerio',
    name: 'Customer.io',
    description: 'Manage messaging, segments, and people in Customer.io',
    longDescription:
      'Connect Customer.io to review campaigns and broadcasts, inspect segments and people, and analyze messaging — all from chat.',
    icon: 'customerio',
    serverUrl: 'https://mcp.customer.io/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['read', 'write'] },
    website: 'https://customer.io',
    docs: 'https://docs.customer.io',
    privacy: 'https://customer.io/legal/privacy-policy/',
    displayTools: [
      'list_campaigns',
      'list_segments',
      'get_person',
      'list_broadcasts',
      'get_metrics',
    ],
  },
  {
    id: 'airtable',
    name: 'Airtable',
    description: 'Read and update bases, tables, and records in Airtable',
    longDescription:
      'Connect Airtable to browse bases and tables, query and update records, and manage comments — all from chat.',
    icon: 'airtable',
    serverUrl: 'https://mcp.airtable.com/mcp',
    transport: 'http',
    auth: {
      type: 'oauth',
      scopes: [
        'data.records:read',
        'data.records:write',
        'schema.bases:read',
        'workspacesAndBases:read',
        'data.recordComments:read',
        'data.recordComments:write',
      ],
    },
    website: 'https://airtable.com',
    docs: 'https://airtable.com/developers/web/mcp',
    privacy: 'https://airtable.com/privacy',
    displayTools: [
      'list_bases',
      'list_tables',
      'list_records',
      'create_record',
      'update_record',
    ],
  },
  {
    id: 'clickup',
    name: 'ClickUp',
    description: 'Manage tasks, lists, and docs in ClickUp',
    longDescription:
      'Connect ClickUp to browse spaces and lists, create and update tasks, and search docs — all from chat.',
    icon: 'clickup',
    serverUrl: 'https://mcp.clickup.com/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['read', 'write'] },
    website: 'https://clickup.com',
    docs: 'https://developer.clickup.com/docs/mcp',
    privacy: 'https://clickup.com/terms/privacy-policy',
    displayTools: [
      'list_tasks',
      'create_task',
      'update_task',
      'list_spaces',
      'list_docs',
    ],
  },
  {
    id: 'todoist',
    name: 'Todoist',
    description: 'Organize tasks and projects in Todoist',
    longDescription:
      'Connect Todoist to view and create tasks, complete and reschedule them, and browse projects — all from chat.',
    icon: 'todoist',
    serverUrl: 'https://ai.todoist.net/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['data:read_write'] },
    website: 'https://todoist.com',
    docs: 'https://developer.todoist.com',
    privacy: 'https://todoist.com/privacy',
    displayTools: [
      'get_tasks',
      'create_task',
      'complete_task',
      'list_projects',
      'update_task',
    ],
  },
  {
    id: 'calcom',
    name: 'Cal.com',
    description: 'Manage bookings, event types, and availability in Cal.com',
    longDescription:
      'Connect Cal.com to review and create bookings, browse event types, and check availability — all from chat.',
    icon: 'calcom',
    serverUrl: 'https://mcp.cal.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://cal.com',
    docs: 'https://cal.com/docs',
    privacy: 'https://cal.com/privacy',
    displayTools: [
      'list_bookings',
      'create_booking',
      'list_event_types',
      'get_availability',
      'cancel_booking',
    ],
  },
  {
    id: 'tally',
    name: 'Tally',
    description: 'Create and manage forms and submissions in Tally',
    longDescription:
      'Connect Tally to browse forms, read submissions and responses, and manage your forms — all from chat.',
    icon: 'tally',
    serverUrl: 'https://mcp.tally.so/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['mcp'] },
    website: 'https://tally.so',
    docs: 'https://tally.so/help',
    privacy: 'https://tally.so/help/privacy-policy',
    displayTools: [
      'list_forms',
      'get_form',
      'list_submissions',
      'get_submission',
      'create_form',
    ],
  },
  {
    id: 'attio',
    name: 'Attio',
    description: 'Manage records, deals, and notes in Attio',
    longDescription:
      'Connect Attio to query and create records, manage deals and lists, and add notes and tasks — all from chat.',
    icon: 'attio',
    serverUrl: 'https://mcp.attio.com/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['mcp', 'openid', 'offline_access'] },
    website: 'https://attio.com',
    docs: 'https://developers.attio.com',
    privacy: 'https://attio.com/legal/privacy',
    displayTools: [
      'list_records',
      'create_record',
      'list_deals',
      'add_note',
      'create_task',
    ],
  },
  {
    id: 'close',
    name: 'Close',
    description: 'Manage leads, opportunities, and activities in Close',
    longDescription:
      'Connect Close to browse leads and opportunities, log activities, and review your sales pipeline — all from chat.',
    icon: 'close',
    serverUrl: 'https://mcp.close.com/mcp',
    transport: 'http',
    auth: {
      type: 'oauth',
      scopes: ['mcp.read', 'mcp.write_safe', 'offline_access'],
    },
    website: 'https://close.com',
    docs: 'https://developer.close.com',
    privacy: 'https://www.close.com/legal/privacy',
    displayTools: [
      'list_leads',
      'create_lead',
      'list_opportunities',
      'log_activity',
      'search_contacts',
    ],
  },
  {
    id: 'cloudinary',
    name: 'Cloudinary',
    description: 'Upload, transform, and search media assets in Cloudinary',
    longDescription:
      'Connect Cloudinary to upload and search media assets, apply transformations, and organize folders — all from chat.',
    icon: 'cloudinary',
    serverUrl: 'https://asset-management.mcp.cloudinary.com/mcp',
    transport: 'http',
    auth: {
      type: 'oauth',
      scopes: ['openid', 'offline_access', 'asset_management', 'upload'],
    },
    website: 'https://cloudinary.com',
    docs: 'https://cloudinary.com/documentation/cloudinary_llm_mcp',
    privacy: 'https://cloudinary.com/privacy',
    displayTools: [
      'upload_asset',
      'search_assets',
      'get_asset',
      'list_folders',
      'transform_asset',
    ],
  },
  {
    id: 'neon',
    name: 'Neon',
    description: 'Query and manage Postgres databases in Neon',
    longDescription:
      'Connect Neon to list projects and databases, run SQL queries, inspect schemas, and manage branches — all from chat.',
    icon: 'neon',
    serverUrl: 'https://mcp.neon.tech/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://neon.tech',
    docs: 'https://neon.tech/docs/ai/neon-mcp-server',
    privacy: 'https://neon.tech/privacy-policy',
    displayTools: [
      'list_projects',
      'run_sql',
      'list_databases',
      'describe_table',
      'list_branches',
    ],
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Run your enabled Zapier actions across 8,000+ apps',
    longDescription:
      'Connect Zapier to run the actions you have enabled in your Zapier MCP — automate tasks across thousands of apps from chat. Choose which actions to expose in your Zapier MCP settings (mcp.zapier.com); the agent then sees exactly those tools.',
    icon: 'zapier',
    serverUrl: 'https://mcp.zapier.com/api/mcp/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['openid', 'profile', 'email'] },
    website: 'https://zapier.com',
    docs: 'https://docs.zapier.com/mcp/home',
    privacy: 'https://zapier.com/privacy',
    displayTools: ['(the Zapier actions you enable in your Zapier MCP)'],
  },
  {
    id: 'ahrefs',
    name: 'Ahrefs',
    description: 'Research backlinks, keywords, and rankings in Ahrefs',
    longDescription:
      'Connect Ahrefs to analyze backlinks, keywords, organic traffic, and competitor rankings — all from chat. Requires a paid Ahrefs plan (Lite or higher).',
    icon: 'ahrefs',
    serverUrl: 'https://api.ahrefs.com/mcp/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://ahrefs.com',
    docs: 'https://docs.ahrefs.com/docs/mcp/reference/introduction',
    privacy: 'https://ahrefs.com/privacy',
    displayTools: [
      'site_explorer',
      'backlinks',
      'keywords_explorer',
      'organic_keywords',
      'rank_tracker',
    ],
  },
  {
    id: 'semrush',
    name: 'Semrush',
    description: 'Analyze SEO, keywords, and competitors in Semrush',
    longDescription:
      'Connect Semrush to research keywords, track rankings, analyze competitors and backlinks, and pull domain analytics — all from chat. Consumes Semrush API units.',
    icon: 'semrush',
    serverUrl: 'https://mcp.semrush.com/v2/mcp',
    transport: 'http',
    auth: { type: 'oauth', scopes: ['mcp.access'] },
    website: 'https://www.semrush.com',
    docs: 'https://developer.semrush.com/api/introduction/semrush-mcp/',
    privacy: 'https://www.semrush.com/company/legal/privacy-policy/',
    displayTools: [
      'domain_overview',
      'keyword_overview',
      'organic_research',
      'backlinks',
      'keyword_magic',
    ],
  },
  {
    id: 'dataforseo',
    name: 'DataForSEO',
    description: 'Access SERP, keyword, and backlink data via DataForSEO',
    longDescription:
      'Connect DataForSEO to pull SERP results, keyword and search-volume data, backlinks, on-page audits, and competitor analytics — all from chat.',
    icon: 'dataforseo',
    serverUrl: 'https://mcp.dataforseo.com/mcp',
    transport: 'http',
    auth: { type: 'oauth' },
    website: 'https://dataforseo.com',
    docs: 'https://dataforseo.com/model-context-protocol',
    privacy: 'https://dataforseo.com/privacy-policy',
    displayTools: [
      'serp_organic',
      'keyword_data',
      'backlinks_summary',
      'on_page',
      'dataforseo_labs',
    ],
  },
];

/** Look up a built-in MCP connector by its registry id. */
export function getBuiltinMcpConnector(
  id: string,
): BuiltinMcpConnector | undefined {
  return BUILTIN_MCP_CONNECTORS.find((c) => c.id === id);
}

/** True if `id` is a known built-in MCP connector. */
export function isBuiltinMcpConnector(id: string): boolean {
  return BUILTIN_MCP_CONNECTORS.some((c) => c.id === id);
}

/**
 * The chat service id / tool-name prefix base for a connector id. Mirrors
 * `mcpServiceId` in src/tools/custom-mcp.ts (slug = drop hyphens) so the
 * connector-gateway reveal and icon mapping agree. Kept here (no server imports)
 * so client code can use it too.
 */
export function builtinMcpServiceId(id: string): string {
  return `mcp_${id.replace(/-/g, '')}`;
}
